import { fetchWithAuth } from '../utils/api';

/**
 * Central event bus for real-time rating notifications and updates
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
type RatingEvent = {
  type: 'RATING_SUBMITTED' | 'STATUS_UPDATE_NEEDED' | 'STATUS_UPDATED' | 'MANUAL_REFRESH_REQUESTED';
  payload: any;
};
/* eslint-enable @typescript-eslint/no-unused-vars */

type EventCallback = (data: any) => void;

interface ParticipantSubmissionStatus {
  user_id: number;
  username: string;
  role: string;
  has_submitted_ratings: boolean;
  timestamp?: string;
}

interface RatingStatusData {
  participants: {
    debaters: ParticipantSubmissionStatus[];
    adjudicators: ParticipantSubmissionStatus[];
    spectators: ParticipantSubmissionStatus[];
  };
  all_ratings_submitted: boolean;
  debaters_rated: boolean;
  panelists_rated_chair: boolean;
  chair_adjudicator: ParticipantSubmissionStatus | null;
  last_updated: string;
}

class RatingEventBus {
  private static instance: RatingEventBus;
  private listeners: Map<string, EventCallback[]> = new Map();
  private submissionStatus: Map<string, Map<number, boolean>> = new Map();
  private ratingStatusCache: Map<string, RatingStatusData> = new Map();
  private mountedComponents: Set<string> = new Set();
  private recentApiCalls: Map<string, number> = new Map();

  private constructor() {
    // Private constructor to enforce singleton
  }

  public static getInstance(): RatingEventBus {
    if (!RatingEventBus.instance) {
      RatingEventBus.instance = new RatingEventBus();
    }
    return RatingEventBus.instance;
  }

  /**
   * Register a component as mounted
   */
  public registerComponent(componentId: string): void {
    this.mountedComponents.add(componentId);
  }

  /**
   * Unregister a component when unmounted
   */
  public unregisterComponent(componentId: string): void {
    this.mountedComponents.delete(componentId);
  }

  /**
   * Check if a component is registered as mounted
   */
  public isComponentMounted(componentId: string): boolean {
    return this.mountedComponents.has(componentId);
  }

  /**
   * Subscribe to a specific event type with component tracking
   */
  public subscribe(eventType: string, callback: EventCallback, componentId?: string): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    
    const eventCallbacks = this.listeners.get(eventType)!;
    
    // Store callback with component ID if provided
    const callbackWrapper = (data: any) => {
      // If component ID provided, only call if component is mounted
      if (!componentId || this.isComponentMounted(componentId)) {
        callback(data);
      }
    };
    
    eventCallbacks.push(callbackWrapper);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        const index = callbacks.indexOf(callbackWrapper);
        if (index !== -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Publish an event to all subscribers
   */
  public publish(eventType: string, data: any): void {
    // For rating submissions, track the status
    if (eventType === 'RATING_SUBMITTED' && data.roundId && data.userId) {
      this.recordSubmission(data.roundId, data.userId);
    }
    
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType)!;
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error(`Error in event listener for ${eventType}:`, e);
        }
      });
    }
  }
  
  /**
   * Record a rating submission
   */
  private recordSubmission(roundId: string | number, userId: number): void {
    const roundKey = String(roundId);
    if (!this.submissionStatus.has(roundKey)) {
      this.submissionStatus.set(roundKey, new Map<number, boolean>());
    }
    
    const roundSubmissions = this.submissionStatus.get(roundKey)!;
    roundSubmissions.set(userId, true);
    
    // Update status cache if available
    this.updateStatusCacheFromSubmission(roundKey, userId);
    
    // Additionally, update session storage directly to ensure status persists
    const cachedStatus = sessionStorage.getItem(`round-${roundKey}-rating-status`);
    if (cachedStatus) {
      try {
        const parsedStatus = JSON.parse(cachedStatus);
        // Update the participant status
        let updated = false;
        
        // Update debaters
        if (parsedStatus.participants && parsedStatus.participants.debaters) {
          for (const debater of parsedStatus.participants.debaters) {
            if (debater.user_id === userId && !debater.has_submitted_ratings) {
              debater.has_submitted_ratings = true;
              debater.timestamp = new Date().toISOString();
              updated = true;
            }
          }
        }
        
        // Update adjudicators
        if (parsedStatus.participants && parsedStatus.participants.adjudicators) {
          for (const adjudicator of parsedStatus.participants.adjudicators) {
            if (adjudicator.user_id === userId && !adjudicator.has_submitted_ratings) {
              adjudicator.has_submitted_ratings = true;
              adjudicator.timestamp = new Date().toISOString();
              updated = true;
            }
          }
        }
        
        if (updated) {
          // Recalculate status flags
          if (parsedStatus.participants) {
            // Check if all debaters have submitted ratings
            parsedStatus.debaters_rated = parsedStatus.participants.debaters.length > 0 && 
              parsedStatus.participants.debaters.every((d: { has_submitted_ratings: any; }) => d.has_submitted_ratings);
            
            // Check if all panelists have submitted ratings
            const panelists = parsedStatus.participants.adjudicators.filter((a: { role: string; }) => a.role === 'Panelist');
            parsedStatus.panelists_rated_chair = panelists.length > 0 && 
              panelists.every((p: { has_submitted_ratings: any; }) => p.has_submitted_ratings);
            
            // All ratings submitted if both conditions are met
            parsedStatus.all_ratings_submitted = parsedStatus.debaters_rated && parsedStatus.panelists_rated_chair;
          }
          
          // Update last updated timestamp
          parsedStatus.last_updated = new Date().toISOString();
          
          // Save back to session storage
          sessionStorage.setItem(`round-${roundKey}-rating-status`, JSON.stringify(parsedStatus));
        }
      } catch (e) {
        console.warn('Failed to update session storage cached status:', e);
      }
    }
    
    // NEW: Make a direct API call to mark the user as having submitted
    this.makeDirectSubmissionCall(roundId, userId);
  }

  /**
   * Make a direct API call to mark submission status
   * This ensures the backend has the latest submission status
   */
  private makeDirectSubmissionCall(roundId: string | number, userId: number): void {
    // Don't await this call - fire and forget to avoid blocking
    try {
      // Try multiple endpoints
      const markSubmitted = async () => {
        const endpoints = [
          `/api/round/${roundId}/mark-submitted/`,
          `/round/${roundId}/mark-submitted/`
        ];
        
        for (const endpoint of endpoints) {
          try {
            await fetchWithAuth(endpoint, {
              method: 'POST'
            });
            console.log(`Successfully marked user ${userId} as submitted for round ${roundId}`);
            return;
          } catch (err) {
            console.warn(`Failed with endpoint ${endpoint}`, err);
          }
        }
        
        console.error(`All submission marking endpoints failed for user ${userId}, round ${roundId}`);
      };
      
      markSubmitted();
    } catch (err) {
      console.error('Error marking submission status:', err);
    }
  }
  
  /**
   * Update cached status when a new submission is recorded
   */
  private updateStatusCacheFromSubmission(roundId: string, userId: number): void {
    const status = this.ratingStatusCache.get(roundId);
    if (!status) return;
    
    let updated = false;
    
    // Update debaters
    for (const debater of status.participants.debaters) {
      if (debater.user_id === userId && !debater.has_submitted_ratings) {
        debater.has_submitted_ratings = true;
        debater.timestamp = new Date().toISOString();
        updated = true;
      }
    }
    
    // Update adjudicators
    for (const adjudicator of status.participants.adjudicators) {
      if (adjudicator.user_id === userId && !adjudicator.has_submitted_ratings) {
        adjudicator.has_submitted_ratings = true;
        adjudicator.timestamp = new Date().toISOString();
        updated = true;
      }
    }
    
    if (updated) {
      // Recalculate status flags
      this.recalculateStatusFlags(status);
      
      // Update cache
      status.last_updated = new Date().toISOString();
      this.ratingStatusCache.set(roundId, status);
      
      // Publish updated status
      this.publish('STATUS_UPDATED', { 
        ...status, 
        roundId, 
        source: 'cache_update',
        timestamp: new Date().toISOString()
      });
    }
  }
  
  /**
   * Recalculate status flags in a status object
   */
  private recalculateStatusFlags(status: RatingStatusData): void {
    // Check if all debaters have submitted ratings
    status.debaters_rated = status.participants.debaters.length > 0 && 
      status.participants.debaters.every(d => d.has_submitted_ratings);
    
    // Check if all panelists have submitted ratings
    const panelists = status.participants.adjudicators.filter(a => a.role === 'Panelist');
    status.panelists_rated_chair = panelists.length > 0 && 
      panelists.every(p => p.has_submitted_ratings);
    
    // All ratings submitted if both conditions are met
    status.all_ratings_submitted = status.debaters_rated && status.panelists_rated_chair;
  }
  
  /**
   * Check if a user has submitted ratings for a round
   */
  public hasSubmitted(roundId: string | number, userId: number): boolean {
    const roundKey = String(roundId);
    if (!this.submissionStatus.has(roundKey)) return false;
    
    const roundSubmissions = this.submissionStatus.get(roundKey)!;
    return !!roundSubmissions.get(userId);
  }
  
  /**
   * Store rating status data
   */
  public setRatingStatus(roundId: string, data: any): void {
    // Clean data for storage
    const statusData: RatingStatusData = {
      participants: data.participants,
      all_ratings_submitted: data.all_ratings_submitted,
      debaters_rated: data.debaters_rated,
      panelists_rated_chair: data.panelists_rated_chair,
      chair_adjudicator: data.chair_adjudicator,
      last_updated: new Date().toISOString()
    };
    
    this.ratingStatusCache.set(roundId, statusData);
    
    // Update our submission tracker with the known submissions
    const submissionMap = new Map<number, boolean>();
    
    // Track debaters
    for (const debater of statusData.participants.debaters) {
      if (debater.has_submitted_ratings) {
        submissionMap.set(debater.user_id, true);
      }
    }
    
    // Track adjudicators
    for (const adjudicator of statusData.participants.adjudicators) {
      if (adjudicator.has_submitted_ratings) {
        submissionMap.set(adjudicator.user_id, true);
      }
    }
    
    this.submissionStatus.set(roundId, submissionMap);
  }
  
  /**
   * Get cached rating status
   */
  public getRatingStatus(roundId: string | number): RatingStatusData | null {
    const roundKey = String(roundId);
    return this.ratingStatusCache.get(roundKey) || null;
  }
  
  /**
   * Get list of participants who have submitted for a round
   */
  public getSubmittedParticipants(roundId: string | number): number[] {
    const roundKey = String(roundId);
    if (!this.submissionStatus.has(roundKey)) return [];
    
    const roundSubmissions = this.submissionStatus.get(roundKey)!;
    return Array.from(roundSubmissions.entries())
      .filter(([_, hasSubmitted]) => hasSubmitted)
      .map(([userId, _]) => userId);
  }

  /**
   * Mark a user as having submitted ratings
   */
  public markUserSubmitted(roundId: string | number, userId: number, username: string, role: string): void {
    
    // Check if this user is already marked as submitted to prevent unnecessary updates
    if (this.hasSubmitted(roundId, userId)) {
      console.log(`User ${userId} already marked as submitted for round ${roundId}, skipping update`);
      return;
    }
    
    // Store submission in local cache
    this.recordSubmission(roundId, userId);
    
    // Update any cached status data
    this.updateCachedStatusForUser(roundId, userId, username, role);
    
    // Send direct API call to server
    this.markSubmissionOnServer(roundId);
  }

  /**
   * Update the cached status for a specific user
   */
  private updateCachedStatusForUser(roundId: string | number, userId: number, username: string, role: string): void {
    // Get cached status directly using String(roundId) instead of creating a separate variable
    const status = this.ratingStatusCache.get(String(roundId));
    
    if (!status) return;
    
    let userFound = false;
    let updated = false;
    
    // Check if user is a debater
    if (status.participants && status.participants.debaters) {
      for (const debater of status.participants.debaters) {
        if (debater.user_id === userId) {
          userFound = true;
          // Only update if the status is changing
          if (!debater.has_submitted_ratings) {
            debater.has_submitted_ratings = true;
            updated = true;
          }
          break; // Exit loop once found
        }
      }
    }
    
    // Check if user is an adjudicator
    if (!userFound && status.participants && status.participants.adjudicators) {
      for (const adjudicator of status.participants.adjudicators) {
        if (adjudicator.user_id === userId) {
          userFound = true;
          // Only update if the status is changing
          if (!adjudicator.has_submitted_ratings) {
            adjudicator.has_submitted_ratings = true;
            updated = true;
          }
          break; // Exit loop once found
        }
      }
    }
    
    // Only proceed with updates if something actually changed
    if (!updated) {
      return; // Skip the rest if nothing was updated
    }
    
    // Recalculate status flags
    if (status.participants) {
      // Check if all debaters have submitted ratings
      status.debaters_rated = status.participants.debaters.length > 0 && 
        status.participants.debaters.every(d => d.has_submitted_ratings);
      
      // Check if all panelists have submitted ratings
      const panelists = status.participants.adjudicators.filter(a => a.role === 'Panelist');
      status.panelists_rated_chair = panelists.length > 0 && 
        panelists.every(p => p.has_submitted_ratings);
      
      // All ratings submitted if both conditions are met
      status.all_ratings_submitted = status.debaters_rated && status.panelists_rated_chair;
    }
    
    // Update cache with new status
    status.last_updated = new Date().toISOString();
    this.ratingStatusCache.set(String(roundId), status);
    
    // Save to session storage
    try {
      sessionStorage.setItem(`round-${roundId}-rating-status`, JSON.stringify(status));
    } catch (e) {
      console.warn('Failed to update session storage:', e);
    }
    
    // Publish update event
    this.publish('STATUS_UPDATED', {
      ...status,
      roundId,
      source: 'local_update', 
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Make a direct API call to mark submission on server
   */
  private markSubmissionOnServer(roundId: string | number): void {
    // Convert roundId to string for consistent handling
    const key = String(roundId);
    const now = Date.now();
    const lastCallTime = this.recentApiCalls.get(key) || 0;
    
    // Prevent calling the API more than once every 5 seconds for the same round
    if (now - lastCallTime < 5000) {
      console.log(`Skipping API call for round ${key} - called too recently`);
      return;
    }
    
    // Record this API call time
    this.recentApiCalls.set(key, now);
    
    // Attempt to send to server - fire and forget
    try {
      const endpoints = [
        `/api/round/${roundId}/mark-submitted/`,
        `/round/${roundId}/mark-submitted/`
      ];
      
      const tryEndpoint = async (index: number) => {
        if (index >= endpoints.length) return;
        
        try {
          await fetchWithAuth(endpoints[index], { method: 'POST' });
          console.log('Successfully marked submission on server');
        } catch (err) {
          console.warn(`Failed to mark submission via ${endpoints[index]}:`, err);
          tryEndpoint(index + 1);
        }
      };
      
      tryEndpoint(0);
    } catch (err) {
      console.error('Error marking submission status:', err);
    }
  }
}

export default RatingEventBus.getInstance();